/**
 * ChatCsvImportModal — the chat-driven CSV import modal (workspace
 * contacts-module lookup/create, per-row draft mapping, the sequential
 * api.objects.createObject loop, the summary toast, and contact-query
 * invalidation).
 *
 * QUARANTINE: this file lives under chatbox/ and re-homes the one already-present
 * `@/modules/contacts` ImportCSVModal cross-layer import (ChatBox +
 * ChatModulePane are the allowlisted cross-layer-import loci) — it moves only
 * that existing import rather than spreading a new cross-layer site. No raw
 * fetch — uses api.* only.
 */
import { ImportCSVModal, type ImportedCSVContact } from '@/modules/contacts/ImportCSVModal';
import { api } from '@/lib/api';
import { MODULE_ALIASES } from '@/types/modules';
import { buildImportFollowUps } from '@/features/chat/utils/import-followups';
import type { QuickAction } from '@/features/chat/hooks/useChat.types';
import type { QueryClient } from '@tanstack/react-query';

export interface ChatCsvImportModalProps {
  file: File;
  token: string | null;
  workspaceId: string;
  showToast: (message: string, type?: 'success' | 'info' | 'error') => void;
  queryClient: QueryClient;
  onClose: () => void;
  /**
   * Close the chat loop after an import actually ran (success OR partial
   * failure). The chat used to go silent here — the agent's "want me to import
   * it?" question + chips just stayed on screen with no confirmation, so the
   * conversation appeared to "hold" instead of showing a done state. The
   * handler posts a confirmation bubble and swaps the now-answered prompt chips
   * for the result-aware follow-up chips (#2014: count + top tag).
   */
  onImported?: (result: {
    imported: number;
    failed: number;
    followUps: QuickAction[];
    /** The contacts actually created — rendered as cards in the chat (#cards). */
    contacts: ImportedContactCard[];
  }) => void;
}

/** Minimal shape for a created-contact card in the chat (mirrors ContactCardData). */
export interface ImportedContactCard {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  title?: string;
}

export function ChatCsvImportModal({
  file,
  token,
  workspaceId,
  showToast,
  queryClient,
  onClose,
  onImported,
}: ChatCsvImportModalProps) {
  return (
    <ImportCSVModal
      initialFile={file}
      onClose={onClose}
      onImport={async (contacts: ImportedCSVContact[]) => {
        // Mirror the per-row path the ⋯ menu's modal uses: POST to
        // the type-specific /contacts endpoint (via api.objects.createObject)
        // for each row. /bulk/objects has a different payload shape
        // (expects `title` not `name`) and would silently create
        // contacts with title='Untitled', so we skip it here.
        //
        // Contacts MUST be linked to at least one module (enforced at
        // apps/api/src/routes/objects.ts:466). Look up the workspace's
        // Contacts module; if none exists, create one. Workspace-
        // level lookup is intentional — the user might be importing
        // into a conversation that has no Contacts panel attached, but
        // a Contacts module elsewhere in the workspace is the right
        // home for these records.
        //
        // CRITICAL: the Contacts MODULE type is `contact_list` (the canonical
        // type ModuleRenderer renders as the Contacts list), NOT `contact`
        // (which is the OBJECT type). Matching/creating `contact` here linked
        // imports to a phantom module the Contacts pane never displays, so the
        // POSTs succeeded (201) but the contacts were invisible. Match the
        // canonical module type so imports land in the Contacts list the user
        // actually sees.
        let contactsModuleId: string | null = null;
        if (workspaceId) {
          try {
            const wsModules = await api.workspaces.listWorkspaceModules(token, workspaceId);
            const existing = wsModules.find((m: any) =>
              ((MODULE_ALIASES[m.type] ?? m.type) as string) === 'contact_list' && !m.archived && !m.archivedAt
            );
            if (existing) {
              contactsModuleId = existing.id;
            } else {
              const created = await api.workspaces.createWorkspaceModule(token, workspaceId, {
                type: 'contact_list',
                label: 'Contacts',
              }, { idempotencyKey: crypto.randomUUID() });
              contactsModuleId = created.id;
            }
          } catch (err) {
            showToast(`Couldn't access Contacts module: ${err instanceof Error ? err.message : String(err)}`, 'error');
            return;
          }
        }
        if (!contactsModuleId) {
          showToast('No workspace selected — open a workspace and try again.', 'error');
          return;
        }
        // Keep each draft paired with its source row so the top-tag tally can be
        // scoped to the rows that were actually created (see the loop below).
        const drafts = contacts
          .map((c) => {
            const name = (c.name || '').trim();
            const emails = Array.isArray(c.emails) ? c.emails.filter(Boolean) : [];
            const fallback = emails[0] || '';
            const finalName = name || fallback;
            if (!finalName) return null;
            const data: any = {
              type: 'contact' as const,
              name: finalName,
              stage: 'lead',
              moduleLinks: [{ moduleId: contactsModuleId }],
            };
            if (emails.length) data.emails = emails;
            if (Array.isArray(c.phones) && c.phones.length) data.phones = c.phones;
            if (c.organization) data.organization = c.organization;
            if (c.role) data.role = c.role;
            if (c.relationship) data.relationship = c.relationship;
            if (c.status) data.status = c.status;
            if (c.location) data.location = c.location;
            if (c.birthday) data.birthday = c.birthday;
            if (c.summary) data.summary = c.summary;
            if (Array.isArray(c.notes) && c.notes.length) {
              data.notes = c.notes.map((content) => ({
                id: crypto.randomUUID(),
                content,
                createdAt: new Date().toISOString(),
                authorId: 'system',
                authorName: 'CSV import',
              }));
            }
            return { data, source: c };
          })
          .filter((d): d is NonNullable<typeof d> => d !== null);
        if (drafts.length === 0) {
          showToast('No valid contacts to import (every row missing name + email).', 'error');
          return;
        }
        let successes = 0;
        let failures = 0;
        let firstError: string | null = null;
        // Collect the created contacts so the chat can render them as cards.
        const createdContacts: ImportedContactCard[] = [];
        for (const { data } of drafts) {
          try {
            const created = await api.objects.createObject(token, data);
            successes++;
            if (created?.id) {
              createdContacts.push({
                id: created.id,
                name: created.name || data.name || '',
                email: created.email || undefined,
                phone: created.phone || undefined,
                company: created.organization || undefined,
                title: created.jobTitle || undefined,
              });
            }
          } catch (err) {
            failures++;
            if (!firstError) firstError = err instanceof Error ? err.message : String(err);
          }
        }
        const summary = failures === 0
          ? `Imported ${successes} contact${successes === 1 ? '' : 's'}.`
          : `Imported ${successes} contact${successes === 1 ? '' : 's'} (${failures} failed${firstError ? `: ${firstError.slice(0, 80)}` : ''}).`;
        showToast(summary, failures === 0 ? 'success' : 'error');
        queryClient.invalidateQueries({
          predicate: (q) => JSON.stringify(q.queryKey).includes('contact'),
        });
        // After a successful import, surface result-aware follow-up chips so the
        // chat doesn't go silent at the most engaged moment (#2014). The distinct
        // company count decides whether "Group by company" is worth offering —
        // it's a no-op when everyone shares one company (or none has one).
        const distinctCompanies = new Set(
          createdContacts
            .map((c) => (c.company ?? '').trim().toLowerCase())
            .filter((c) => c.length > 0),
        ).size;
        const followUps =
          successes > 0
            ? buildImportFollowUps({
                imported: successes,
                failed: failures,
                objectType: 'contact',
                distinctCompanies,
              })
            : [];
        // Close the chat loop: hand the outcome back so ChatBox can post a
        // confirmation bubble and replace the answered prompt chips. The toast
        // is transient; without this the conversation just held the agent's
        // "want me to import it?" question with no resolution.
        onImported?.({ imported: successes, failed: failures, followUps, contacts: createdContacts });
        onClose();
      }}
    />
  );
}
